import type { IpcRendererEvent } from "electron";

import { DownloadableAsset } from "../model/Asset";
import AssetDownloadTarget from "../model/AssetDownloadTarget";
import BarrelType from "../model/BarrelType";
import Color from "../model/color";
import Font from "../model/font";
import { PLATFORM } from "../enums";

import ElectronAppFeature from "./ElectronAppFeature";
import IpcChannel, { dynamicIpcChannels } from "./IpcChannel";
import { AssetContentFormat } from "../api/model/snapshots/Asset";

interface ElectronMirror {
    actions: {
        sendToggleMaximize: () => void;
        sendUnreadNotifications: (icon: string) => void;
        sendReadNotifications: () => void;
        unregisterGetMenuTemplateListeners: () => void;
        onGetMenuTemplate: (listener: unknown) => void;
        sendMenuTemplateV3: (params: unknown) => void;
        sendGetAppMenuParameters: () => void;
        sendOnLogin: (user: unknown) => void;
        sendRestartApp: () => void;
        sendRemindUpdateLater: (version: string) => void;
        onAppUpdateDownloaded: (listener: unknown) => void;
        onPluginUpdateDownloaded: (listener: unknown) => void;
        sendInstallPluginUpdate: (updateInfo: unknown) => void;
        sendRemindPluginInstallLater: (updateInfo: unknown) => void;
        onCloseAllBanners: (listener: unknown) => void;
        unregisterAppUpdateDownloadedListeners: () => void;
        onceSsoFlowInitiated: (listener: unknown) => void;
        unregisterBarrelWorkspacePathListeners: (bid: string) => void;
        onceSetBarrelWorkspacePath: (bid: string, listener: unknown) => void;
        unregisterEditorInfoListeners: () => void;
        onceEditorInfo: (listener: unknown) => void;
        onceRedeemSlackCode: (listener: unknown) => void;
        sendAddToSlack: (params: { id: string; barrelType: BarrelType; name: string; }) => void;
        sendQueryEditorInfo: (params: { bid: string; }) => void;
        sendSetDefaultEditor: (params: { extension: string; editor: unknown; }) => void;
        sendSelectConnectedComponentsWorkspaceFolder: (params: { bid: string; }) => void;
        sendOpenFileInEditor: (params: { bid: string; filePath: string; editor: unknown;}) => void;
        unregisterSourceFileListeners: (versionId: string | undefined | null) => void;
        onceVersionIdSourceFile: (versionId: string | undefined | null, listener: unknown) => void;
        sendQuerySourceFile: (versionId: string | undefined | null) => void;
        sendOpenItem: (path: string) => void;
        sendShowItemInFolder: (path: string) => void;
        sendOpenPopout: (params: { name: string; width: number; height: number; url: string; }, bid: string) => void;
        sendHideSuggestedColors: (params: { id: string; colors: Color[]; }) => void;
        sendHideSuggestedTextStyles: (params: { id: string; textStyles: Font[]; }) => void;
        sendRetryImport: (versionImportId: string) => void;
        sendCancelImport: (versionImportId: string) => void;
        sendRetryGettingProject: (versionImportId: string) => void;
        unregisterImportsChangeListeners: () => void;
        onImportsChange: (listener: unknown) => void;
        sendSubscribeToImports: () => void;
        sendRemoveComponentLimitRejection: (barrelId: string) => void;
        sendClearImports: () => void;
        onImportsStarted: (listener: unknown) => void;
        onSetSuggestedResources: (listener: unknown) => void;
        onApplyColorOperations: (listener: unknown) => void;
        onImportsStartedFromOldXdIntegration: (listener: unknown) => void;
        onCreateScreen: (listener: unknown) => void;
        onCreateScreenVersion: (listener: unknown) => void;
        onCreateProjectComponent: (listener: unknown) => void;
        onCreateProjectComponentVersion: (listener: unknown) => void;
        onCreateProjectComponentVersionV2: (listener: unknown) => void;
        onCreateProjectComponentSection: (listener: unknown) => void;
        onUpdateProjectComponentSection: (listener: unknown) => void;
        onUpdateProjectComponentsOrder: (listener: unknown) => void;
        onCreateProjectColor: (listener: unknown) => void;
        onCreateStyleguideColor: (listener: unknown) => void;
        onUpdateProjectColor: (listener: unknown) => void;
        onUpdateStyleguideColor: (listener: unknown) => void;
        onCreateProjectTextStyle: (listener: unknown) => void;
        onCreateStyleguideTextStyle: (listener: unknown) => void;
        onUpdateProjectTextStyle: (listener: unknown) => void;
        onUpdateStyleguideTextStyle: (listener: unknown) => void;
        onCreateStyleguideComponent: (listener: unknown) => void;
        onCreateStyleguideComponentVersion: (listener: unknown) => void;
        onCreateStyleguideComponentVersionV2: (listener: unknown) => void;
        onCreateStyleguideComponentSection: (listener: unknown) => void;
        onUpdateStyleguideComponentSection: (listener: unknown) => void;
        onUpdateStyleguideComponentsOrder: (listener: unknown) => void;
        onAuthProjectSlack: (listener: unknown) => void;
        onAuthStyleguideSlack: (listener: unknown) => void;
        sendInitialImportToBarrelData: (params: unknown) => void;
        unregisterInitialImportToBarrelDataListeners: () => void;
        onInitialImportToBarrelData: (listener: unknown) => void;
        onLoadUrl: (listener: unknown) => void;
        onLoadAppUri: (listener: unknown) => void;
        onGetMenuTemplateV1: (listener: unknown) => void;
        sendMenuTemplateV1: (menuTemplate: unknown) => void;
        onGetMenuTemplateV2: (listener: unknown) => void;
        sendMenuTemplateV2: (menuTemplate: unknown) => void;
        unregisterMenuItemClickedListeners: () => void;
        onMenuItemClicked: (listener: unknown) => void;
        // Sends a request to electron to get asset download target, the response is received via `onAssetDownloadTarget`
        sendQueryAssetDownloadTarget: (platform: PLATFORM) => void;
        // Creates a listener to save asset download target
        onAssetDownloadTarget: (
            platform: PLATFORM,
            listener: (
                ev: IpcRendererEvent,
                downloadTargets: AssetDownloadTarget[]
            ) => void
        ) => void;
        // Removes the listener that `onAssetDownloadTarget` creates
        unregisterAssetDownloadTargetListeners: (platform: PLATFORM) => void;
        // Sends a request to electron to download assets, the response is received via `onceAssetsDownloaded`
        sendDownloadAssets: (params: {
            platform: PLATFORM;
            assets: DownloadableAsset[];
            format: AssetContentFormat;
            namingConvention: string;
            downloadTarget?: AssetDownloadTarget;
            shouldBrowseNewFolder?: boolean;
            // These fields are used in the old version (<= 10.7.0)
            projectType: string;
            ignoreProjectFolderPersistence?: boolean;
        }) => void;
        // Creates a listener to execute a callback after assets are downloaded
        onceAssetsDownloaded: (
            listener: (
                ev: IpcRendererEvent,
                downloadTarget: AssetDownloadTarget
            ) => void
        ) => void;
        // Removes the listener that `onceAssetsDownloaded` creates
        unregisterAssetsDownloadedListeners: () => void;
        sendDeleteAssetDownloadTarget: (platform: PLATFORM, targetId: string) => void;
        unregisterContextMenuItemClickedListener: () => void;
        unregisterContextMenuClosedListeners: () => void;
        sendContextMenuTemplate: (template: unknown) => void;
        onContextMenuItemClicked: (listener: unknown) => void;
        onContextMenuClosed: (listener: unknown) => void;
        sendOpenExternalUrl: (url: string) => void;
        onSetFullScreen: (listener: unknown) => void;
        sendOpenMenu: () => void;
        sendMinimize: () => void;
        sendClose: () => void;
        sendUpdateThemeAppearance: (theme: string) => void;
        sendImportPNGs: (barrelId: string, organizationId: string) => void;
        sendUpdateHeaderDragRegions: (dragRegions: unknown) => void;
        onSetWindowsMaximize: (listener: unknown) => void;
        onUpdateUseDesktopAppForLinks: (listener: unknown) => void;
        sendSetPlatform: (platform: PLATFORM) => void;
    };
    features: Record<ElectronAppFeature, boolean>;
}

let electronMirror: ElectronMirror | undefined;

if (typeof window.require === "function") { // This will be removed, we'll force update old versions
    const electronMirrorFromMainWorld = window.require("electron");
    electronMirror = {
        // ipcRenderer: electronMirrorFromMainWorld.ipcRenderer,
        actions: {
            sendToggleMaximize() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ToggleMaximize);
            },
            sendUnreadNotifications(icon) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.UnreadNotifications, icon);
            },
            sendReadNotifications() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ReadNotifications);
            },
            unregisterGetMenuTemplateListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.GetMenuTemplateV3);
            },
            onGetMenuTemplate(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.GetMenuTemplateV3, listener);
            },
            sendMenuTemplateV3(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.MenuTemplateV3, params);
            },
            sendGetAppMenuParameters() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.GetAppMenuParameters);
            },
            sendOnLogin(user) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OnLogin, user);
            },
            sendRestartApp() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RestartApp);
            },
            sendRemindUpdateLater(version) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RemindUpdateLater, version);
            },
            onAppUpdateDownloaded(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.AppUpdateDownloaded, listener);
            },
            onPluginUpdateDownloaded(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.PluginUpdateDownloaded, listener);
            },
            sendInstallPluginUpdate(updateInfo) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.InstallPluginUpdate, updateInfo);
            },
            sendRemindPluginInstallLater(updateInfo) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RemindPluginInstallLater, updateInfo);
            },
            onCloseAllBanners(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CloseAllBanners, listener);
            },
            onceSsoFlowInitiated(listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(IpcChannel.SsoFlowInitiated, listener);
            },
            unregisterAppUpdateDownloadedListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.AppUpdateDownloaded);
            },
            unregisterBarrelWorkspacePathListeners(bid) {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(`${bid}-workspace-path`);
            },
            onceSetBarrelWorkspacePath(bid, listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(`${bid}-workspace-path`, listener);
            },
            unregisterEditorInfoListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.EditorInfo);
            },
            onceEditorInfo(listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(IpcChannel.EditorInfo, listener);
            },
            onceRedeemSlackCode(listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(IpcChannel.RedeemSlackCode, listener);
            },
            sendAddToSlack(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.AddToSlack, params);
            },
            sendQueryEditorInfo(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.QueryEditorInfo, params);
            },
            sendSetDefaultEditor(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.SetDefaultEditor, params);
            },
            sendSelectConnectedComponentsWorkspaceFolder(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(
                    IpcChannel.SelectConnectedComponentsWorkspaceFolder,
                    params
                );
            },
            sendOpenFileInEditor(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OpenFileInEditor, params);
            },
            unregisterSourceFileListeners(versionId) {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(`${versionId}:sourceFile`);
            },
            onceVersionIdSourceFile(versionId, listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(`${versionId}:sourceFile`, listener);
            },
            sendQuerySourceFile(versionId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.QuerySourceFile, versionId);
            },
            sendOpenItem(path) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OpenItem, path);
            },
            sendShowItemInFolder(path) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ShowItemInFolder, path);
            },
            sendOpenPopout(params, bid) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OpenPopout, params, bid);
            },
            sendHideSuggestedColors(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.HideSuggestedColors, params);
            },
            sendHideSuggestedTextStyles(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.HideSuggestedTextStyles, params);
            },
            sendRetryImport(versionImportId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RetryImport, versionImportId);
            },
            sendCancelImport(versionImportId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.CancelImport, versionImportId);
            },
            sendRetryGettingProject(versionImportId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RetryGettingProject, versionImportId);
            },
            unregisterImportsChangeListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.ImportsChange);
            },
            onImportsChange(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ImportsChange, listener);
            },
            sendSubscribeToImports() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.SubscribeToImports);
            },
            sendRemoveComponentLimitRejection(barrelId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.RemoveComponentLimitRejection, barrelId);
            },
            sendClearImports() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ClearImports);
            },
            onImportsStarted(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ImportsStarted, listener);
            },
            onSetSuggestedResources(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.SetSuggestedResources, listener);
            },
            onApplyColorOperations(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ApplyColorOperations, listener);
            },
            onImportsStartedFromOldXdIntegration(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ImportStartedFromOldXdIntegration, listener);
            },
            onCreateScreen(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateScreen, listener);
            },
            onCreateScreenVersion(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateScreenVersion, listener);
            },
            onCreateProjectComponent(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectComponent, listener);
            },
            onCreateProjectComponentVersion(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectComponentVersion, listener);
            },
            onCreateProjectComponentVersionV2(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectComponentVersionV2, listener);
            },
            onCreateProjectComponentSection(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectComponentSection, listener);
            },
            onUpdateProjectComponentSection(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateProjectComponentSection, listener);
            },
            onUpdateProjectComponentsOrder(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateProjectComponentsOrder, listener);
            },
            onCreateProjectColor(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectColor, listener);
            },
            onCreateStyleguideColor(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideColor, listener);
            },
            onUpdateProjectColor(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateProjectColor, listener);
            },
            onUpdateStyleguideColor(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateStyleguideColor, listener);
            },
            onCreateProjectTextStyle(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateProjectTextStyle, listener);
            },
            onCreateStyleguideTextStyle(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideTextStyle, listener);
            },
            onUpdateProjectTextStyle(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateProjectTextStyle, listener);
            },
            onUpdateStyleguideTextStyle(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateStyleguideTextStyle, listener);
            },
            onCreateStyleguideComponent(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideComponent, listener);
            },
            onCreateStyleguideComponentVersion(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideComponentVersion, listener);
            },
            onCreateStyleguideComponentVersionV2(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideComponentVersionV2, listener);
            },
            onCreateStyleguideComponentSection(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.CreateStyleguideComponentSection, listener);
            },
            onUpdateStyleguideComponentSection(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateStyleguideComponentSection, listener);
            },
            onUpdateStyleguideComponentsOrder(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.UpdateStyleguideComponentsOrder, listener);
            },
            onAuthProjectSlack(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.AuthProjectSlack, listener);
            },
            onAuthStyleguideSlack(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.AuthStyleguideSlack, listener);
            },
            sendInitialImportToBarrelData(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.InitialImportToBarrelData, params);
            },
            unregisterInitialImportToBarrelDataListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.InitialImportToBarrelData);
            },
            onInitialImportToBarrelData(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.InitialImportToBarrelData, listener);
            },
            onLoadUrl(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.LoadUrl, listener);
            },
            onLoadAppUri(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.LoadAppUri, listener);
            },
            onGetMenuTemplateV1(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.GetMenuTemplateV1, listener);
            },
            sendMenuTemplateV1(menuTemplate) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.MenuTemplateV1, menuTemplate);
            },
            onGetMenuTemplateV2(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.GetMenuTemplateV2, listener);
            },
            sendMenuTemplateV2(menuTemplate) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.MenuTemplateV2, menuTemplate);
            },
            unregisterMenuItemClickedListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.MenuItemClicked);
            },
            onMenuItemClicked(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.MenuItemClicked, listener);
            },
            sendQueryAssetDownloadTarget(platform) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.QueryAssetDownloadTarget, platform);
            },
            onAssetDownloadTarget(platform, listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(
                    dynamicIpcChannels.assetDownloadTargets(platform),
                    listener
                );
            },
            unregisterAssetDownloadTargetListeners(platform: PLATFORM) {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(
                    dynamicIpcChannels.assetDownloadTargets(platform)
                );
            },
            sendDownloadAssets(params) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.DownloadAssets, params);
            },
            onceAssetsDownloaded(listener) {
                electronMirrorFromMainWorld.ipcRenderer.once(IpcChannel.AssetsDownloaded, listener);
            },
            unregisterAssetsDownloadedListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.AssetsDownloaded);
            },
            sendDeleteAssetDownloadTarget(platform, targetId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.DeleteAssetDownloadTarget, platform, targetId);
            },
            unregisterContextMenuItemClickedListener() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.ContextMenuItemClicked);
            },
            unregisterContextMenuClosedListeners() {
                electronMirrorFromMainWorld.ipcRenderer.removeAllListeners(IpcChannel.ContextMenuClosed);
            },
            sendContextMenuTemplate(template) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ContextMenuTemplate, template);
            },
            onContextMenuItemClicked(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ContextMenuItemClicked, listener);
            },
            onContextMenuClosed(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.ContextMenuClosed, listener);
            },
            sendOpenExternalUrl(url) {
                if (electronMirrorFromMainWorld.shell) {
                    electronMirrorFromMainWorld.shell.openExternal(url);
                } else {
                    electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OpenExternalUrl, url);
                }
            },
            onSetFullScreen(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(IpcChannel.SetFullScreen, listener);
            },
            sendOpenMenu() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.OpenMenu);
            },
            sendMinimize() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.Minimize);
            },
            sendClose() {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.Close);
            },
            sendUpdateThemeAppearance(theme) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.UpdateThemeAppearance, theme);
            },
            sendImportPNGs(barrelId, organizationId) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.ImportPNGs, barrelId, organizationId);
            },
            sendUpdateHeaderDragRegions(dragRegions) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.UpdateHeaderDragRegions, dragRegions);
            },
            onSetWindowsMaximize(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(
                    IpcChannel.SetWindowsMaximize,
                    listener
                );
            },
            onUpdateUseDesktopAppForLinks(listener) {
                electronMirrorFromMainWorld.ipcRenderer.on(
                    IpcChannel.UpdateUseDesktopAppForLinks,
                    listener
                );
            },
            sendSetPlatform(platform) {
                electronMirrorFromMainWorld.ipcRenderer.send(IpcChannel.SetPlatform, platform);
            }
        },
        features: electronMirrorFromMainWorld.features
    };
    /* @ts-ignore */
} else if (window.desktopAppApi) {
    /* @ts-ignore */
    electronMirror = window.desktopAppApi;
}

export default electronMirror;
